/**
 * Report-action nudge — a once-per-report reminder that the caller has reports awaiting their
 * verification. Surfaced only on `rekor login` and `rekor status` (both already authenticated and
 * infrequent), so there's no per-command polling and thus no rate-limit pressure.
 *
 * Actionable states for a reporter:
 *   - `shipped`                      → accept or contest the fix
 *   - `dismissed` (not final)        → contest the dismissal
 * (`addressed` is terminal — nothing to do.)
 *
 * The shipped EMAIL is the primary, reliable channel; this is a best-effort secondary reminder for
 * CLI users. Deduped per `<report_id>:<status>` key in ~/.config/rekor/config.json.
 */

import { ApiClient } from './client.js';
import { loadConfig, saveConfig } from './config.js';
import { isCI } from './env.js';

const MAX_TRACKED = 200;

// Reporter-facing summary shape (mirrors the backend's toReporterSummary). dismissal_final is a
// boolean; contest_count discriminates re-shipments so a re-shipped fix re-nudges.
interface ReporterSummary {
  report_id: string;
  status: string;
  contest_count: number;
  dismissal_final: boolean;
}

/**
 * Print a one-time nudge for reports awaiting the caller's accept/contest, if any are new.
 * Best-effort and interactive-only: never throws, never prints in non-TTY/CI contexts. Safe to call
 * unconditionally after auth — a `rec_`-token caller (no reporter surface) just yields nothing.
 */
export async function nudgeReportActions(): Promise<void> {
  if (!process.stdout.isTTY || isCI()) return;
  try {
    const client = new ApiClient();
    const [shipped, dismissed] = await Promise.all([
      client.request<ReporterSummary[]>('GET', '/v1/reports?status=shipped'),
      client.request<ReporterSummary[]>('GET', '/v1/reports?status=dismissed'),
    ]);

    const actionable = [
      // Shipped re-fires per shipment: a contested fix that's re-shipped is a NEW actionable event
      // (the backend clears notified_at_shipped on `contested`, so the email re-fires). Key on
      // contest_count — it increments once per re-ship cycle — so the nudge tracks the email channel
      // rather than going silent after the first shipment.
      ...(shipped ?? []).map((r) => ({
        id: r.report_id,
        status: r.status,
        key: `${r.report_id}:shipped:${r.contest_count}`,
        verb: 'accept or contest',
      })),
      // A `final` dismissal can't be contested — don't nudge for it. Keyed once-ever to match the
      // dismissed email, whose slot is never re-armed.
      ...(dismissed ?? [])
        .filter((r) => !r.dismissal_final)
        .map((r) => ({ id: r.report_id, status: r.status, key: `${r.report_id}:dismissed`, verb: 'contest' })),
    ];
    if (actionable.length === 0) return;

    const config = loadConfig();
    const seen = new Set(config.nudged_report_ids ?? []);
    const fresh = actionable.filter((a) => !seen.has(a.key));
    if (fresh.length === 0) return;

    const n = fresh.length;
    console.error(`\n📬 ${n} report${n > 1 ? 's' : ''} awaiting your review:`);
    for (const a of fresh) {
      console.error(`   • ${a.id} (${a.status}) — ${a.verb}`);
    }
    console.error('   → `rekor report list --status shipped`, then `rekor report accept <id>` or `rekor report contest <id> --reason "..."`');

    const updated = [...seen, ...fresh.map((a) => a.key)];
    saveConfig({ ...config, nudged_report_ids: updated.slice(-MAX_TRACKED) });
  } catch {
    /* best-effort: a nudge must never break login/status */
  }
}
